import { isEmpty } from 'lodash';
import { useContext, useMemo } from 'react';
import ChatBubblesSquareIcon from '@shared/assets/icons/v2/chat-bubbles-square.svg';
import { MBButton } from '@shared/components/MBButton';
import { View } from '@shared/components/View';
import { MBBoxedIcon, MBImage } from '@shared/components/image';
import { SelectableRadioDot } from '@shared/components/list';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBScrollView } from '@shared/components/scrollview';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import { useLinkingOptions } from '@shared/scenes/workflows/edit/components/optionsMenu/relatedCardOptions/useLinkingOptions';
import { WorkspaceDetail, useListWorkflowTemplates } from '@shared/scenes/workflows/hooks';
import { ColorThemeContext, grid } from '@shared/styles';
import { useDebouncedState } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { LinkableWorkflow } from './RelatedCardLinkingOptions';

interface Props {
  component: RelatedCardComponent;
  workspaceDetails: Map<string, WorkspaceDetail>;
}

export function RelatedCardLinkingView({ component, workspaceDetails }: Props) {
  const colors = useContext(ColorThemeContext);
  const [searchKeyword, setSearchKeyword] = useDebouncedState('', 300);

  const { workflowTemplates, loading, hasMore, loadMore } = useListWorkflowTemplates({
    input: { isSubform: false, name: !isEmpty(searchKeyword) ? searchKeyword : undefined },
  });
  const linkableWorkflows = useMemo(
    () =>
      !isEmpty(workflowTemplates)
        ? ([...(workflowTemplates ?? [])].map(template => {
            return {
              workflowTemplateId: template.id,
              imageUrl: template.channelDefaultConfiguration?.imageUrl,
              name: workspaceDetails.get(template.id)?.name ?? template.name,
              components: template.components,
            };
          }) as LinkableWorkflow[])
        : [],
    [workflowTemplates, workspaceDetails],
  );
  const selectedWorkflowTemplateId = component.workflowTemplateId;
  const selectedWorkflow = linkableWorkflows?.find(value => {
    return value?.workflowTemplateId === selectedWorkflowTemplateId;
  });

  const { onSelectWorkflow } = useLinkingOptions(component);

  return (
    <View paddingBottom={2}>
      <View paddingHorizontal={2} paddingVertical={2}>
        <MBTextOverlineBold color={colors.foregroundInactive}>
          {i18n('builder.relatedCardOptions.responseOptions')}
        </MBTextOverlineBold>
      </View>
      <View paddingHorizontal={2} paddingBottom={2}>
        <MBSearchBar
          value={searchKeyword}
          onChangeText={setSearchKeyword}
          placeholder={i18n('common.search')}
        />
      </View>
      <MBScrollView style={{ maxHeight: grid(30) }}>
        {loading && (
          <View paddingVertical={4}>
            <MBLoadingIndicator />
          </View>
        )}
        {linkableWorkflows?.map(workflow => {
          return (
            <SelectWorkflowRow
              key={workflow.workflowTemplateId}
              workflow={workflow}
              isSelected={workflow?.workflowTemplateId === selectedWorkflow?.workflowTemplateId}
              onPress={onSelectWorkflow}
            />
          );
        })}
        {hasMore && (
          <View padding={2}>
            <MBButton
              theme="outline"
              title={i18n('card.list.loadMore')}
              onPress={loadMore}
              style={{ alignSelf: 'center' }}
              size="small"
            />
          </View>
        )}
      </MBScrollView>
    </View>
  );
}

interface WorkflowSelectionRowProps {
  workflow: LinkableWorkflow;
  isSelected: boolean;
  onPress(id: string);
}

function SelectWorkflowRow({ workflow, isSelected, onPress }: WorkflowSelectionRowProps) {
  const colors = useContext(ColorThemeContext);
  return (
    <MBTouchableOpacity onPress={() => onPress(workflow.workflowTemplateId)}>
      <View row centerVertically paddingVertical={0.5} paddingHorizontal={2}>
        {isEmpty(workflow.imageUrl) && (
          <MBBoxedIcon
            source={ChatBubblesSquareIcon}
            color={colors.avatar.foreground}
            backgroundColor={colors.avatar.background}
            size={'small'}
          />
        )}
        {!isEmpty(workflow.imageUrl) && (
          <MBImage
            source={{ uri: workflow.imageUrl }}
            style={{ height: grid(3), width: grid(3) }}
          />
        )}
        <View fill centerVertically paddingLeft={1}>
          <MBTextBody1>{workflow.name}</MBTextBody1>
        </View>
        <SelectableRadioDot selected={isSelected} radio={false} />
      </View>
    </MBTouchableOpacity>
  );
}
